'use client';

import Link from 'next/link';
import { useCallback, useState } from 'react';
import OnboardingModal from './OnboardingModal';

/**
 * WelcomeNav — фиксированный navbar для /welcome.
 * Logo + десктопная навигация (3 anchor-ссылки) + единая кнопка «Начать»,
 * которая открывает OnboardingModal с инструкцией и выбором канала (TG/VK).
 * Раньше тут были две отдельные иконки TG/VK — но они читались как
 * «соцсети/контакты», а не как primary CTA «попробовать продукт».
 */

const NAV_ITEMS: { label: string; targetId: string }[] = [
  { label: 'Что умеет',  targetId: 'scene-categories' },
  { label: 'Подборки',   targetId: 'scene-stories' },
  { label: 'Тарифы',     targetId: 'scene-pricing' },
  { label: 'Вопросы',    targetId: 'scene-faq' },
];

export default function WelcomeNav({ botUrl, vkUrl }: { botUrl: string; vkUrl: string }) {
  const [modalOpen, setModalOpen] = useState(false);

  const scrollTo = useCallback((id: string) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#f3fcf0]/90 backdrop-blur-sm border-b border-[#c8d5c6]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-14 flex items-center justify-between gap-4">
          {/* Лого */}
          <Link href="/" className="flex items-center gap-2.5 shrink-0">
            <div className="w-7 h-7 rounded-lg bg-[#b7102a] flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <span className="font-semibold text-[#161d16] text-sm tracking-tight font-display">Favbox</span>
          </Link>

          {/* Десктопная навигация */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.targetId}
                type="button"
                onClick={scrollTo(item.targetId)}
                className="px-3.5 py-1.5 rounded-full text-[#4a5a49] hover:text-[#161d16] hover:bg-[#e7f0e5] text-sm font-medium font-body transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Единая CTA «Начать» — открывает OnboardingModal */}
          <button
            type="button"
            onClick={openModal}
            aria-haspopup="dialog"
            aria-expanded={modalOpen}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white text-sm font-semibold transition-all cursor-pointer font-body focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b7102a]/40"
            style={{ boxShadow: '0 8px 20px -6px rgba(183,16,42,0.45)' }}
          >
            {/* Sparkle-иконка слева */}
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2l1.6 5.3L19 9l-5.4 1.7L12 16l-1.6-5.3L5 9l5.4-1.7L12 2zM19 14l.8 2.7L22 17l-2.2.3L19 20l-.8-2.7L16 17l2.2-.3L19 14zM5 16l.6 1.7L7 18l-1.4.3L5 20l-.6-1.7L3 18l1.4-.3L5 16z" />
            </svg>
            Начать
          </button>
        </div>
      </header>

      <OnboardingModal open={modalOpen} onClose={closeModal} botUrl={botUrl} vkUrl={vkUrl} />
    </>
  );
}
